import React, { useRef, useState } from 'react';
import { Send, Globe, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScroll, useTransform, motion } from 'framer-motion';
import logoImg from '../assets/logo.png';
import ApplicationModal from './ApplicationModal';

const footerLinks = [
  { name: 'Ana Sayfa', href: '/' },
  { name: 'Hakkımızda', href: '/hakkimizda' },
  { name: 'Maçlar', href: '/maclar' },
  { name: 'Eğitim', href: '/egitim' },
];

const helpLinks = ['Sıkça Sorulan Sorular', 'İletişim', 'Güvenlik', 'KVKK'];

const Footer: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"]
  });

  const contentY = useTransform(scrollYProgress, [0, 1], ["-20%", "0%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.7, 1]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.97, 1]);

  return (
    <>
      <style>{`
        .footer-link { color: #0f2d5e; text-decoration: none; font-size: 0.875rem; font-weight: 600; transition: all 0.3s ease; display: inline-block; }
        .footer-link:hover { color: #e65100; transform: translateX(5px); }
        .footer-social-btn { 
          width: 48px; height: 48px; border-radius: 14px; 
          background: #fff; border: 1px solid #f1f5f9; 
          display: flex; align-items: center; justify-content: center;
          color: #64748b; transition: all 0.3s ease; 
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
        }
        .footer-social-btn:hover { color: #e65100; border-color: #e65100; transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(230,81,0,0.1); }
        
        .footer-input { 
          background: #fff; border: 1px solid #e2e8f0; 
          padding: 14px 20px; font-size: 0.875rem; color: #1e293b; 
          width: 100%; outline: none; border-radius: 12px 0 0 12px;
          font-family: var(--font-body); transition: all 0.3s ease; 
        }
        .footer-input:focus { border-color: #e65100; box-shadow: 0 0 0 4px rgba(230,81,0,0.05); }
        
        .footer-submit { 
          background: linear-gradient(135deg, #e65100 0%, #ff9800 100%);
          border: none; padding: 0 24px; 
          cursor: pointer; color: #fff; transition: all 0.3s ease; 
          border-radius: 0 12px 12px 0; display: flex; align-items: center; 
        }
        .footer-submit:hover { 
          background: linear-gradient(135deg, #ff9800 0%, #e65100 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(230,81,0,0.2);
        }
        
        .footer-wrapper {
          position: relative;
          background: #fff;
          overflow: hidden;
        }

        .footer-bg-animation {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          z-index: 1;
          overflow: hidden;
          background: #fff;
        }

        .blob {
          position: absolute;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, #bae6fd 0%, transparent 70%);
          filter: blur(50px);
          opacity: 0.6;
          border-radius: 50%;
          z-index: 1;
        }

        .blob-1 {
          top: -200px;
          left: -100px;
          animation: blob-float-1 25s infinite linear;
        }

        .blob-2 {
          bottom: -200px;
          right: -100px;
          background: radial-gradient(circle, #7dd3fc 0%, transparent 70%);
          animation: blob-float-2 30s infinite linear;
        }

        .blob-3 {
          top: 20%;
          left: 40%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, #e0f2fe 0%, transparent 70%);
          animation: blob-float-3 20s infinite linear;
        }

        @keyframes blob-float-1 {
          0% { transform: translate(-20%, -20%) rotate(0deg) scale(1); }
          33% { transform: translate(120%, 40%) rotate(120deg) scale(1.2); }
          66% { transform: translate(40%, 120%) rotate(240deg) scale(0.8); }
          100% { transform: translate(-20%, -20%) rotate(360deg) scale(1); }
        }

        @keyframes blob-float-2 {
          0% { transform: translate(20%, 20%) rotate(0deg) scale(1.1); }
          50% { transform: translate(-120%, -60%) rotate(-180deg) scale(0.9); }
          100% { transform: translate(20%, 20%) rotate(-360deg) scale(1.1); }
        }

        @keyframes blob-float-3 {
          0% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-80%, 80%) scale(1.4); }
          50% { transform: translate(80%, -80%) scale(1.1); }
          75% { transform: translate(-40%, -40%) scale(1.3); }
          100% { transform: translate(0, 0) scale(1); }
        }

        @media (max-width: 767px) {
          .footer-grid { 
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 2.5rem 1.25rem !important;
            margin-bottom: 3rem !important;
          }
          .footer-brand-col, .footer-course-col {
            grid-column: span 2 / span 2 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
          }
          .footer-brand-col img { width: 56px !important; height: 56px !important; }
          .footer-brand-col span { font-size: 20px !important; }
          .footer-brand-col p { font-size: 0.9rem !important; line-height: 1.6 !important; max-width: 100% !important; }
          
          .footer-social-btn { width: 40px !important; height: 40px !important; border-radius: 10px !important; }
          .footer-social-btn svg { width: 18px !important; height: 18px !important; }
          
          .footer-grid h4 { font-size: 11px !important; margin-bottom: 1rem !important; padding-top: 0 !important; }
          .footer-link { font-size: 0.875rem !important; }
          .footer-grid ul { gap: 0.8rem !important; }
          
          .footer-course-col p { font-size: 0.9rem !important; margin-bottom: 1.5rem !important; }
          .footer-submit { height: 40px !important; padding: 0 24px !important; font-size: 11px !important; }
          
          .footer-bottom { padding-top: 1.5rem !important; gap: 1rem !important; }
          .footer-bottom-text {
            font-size: 11px !important;
            letter-spacing: 0.05em !important;
          }
          .footer-grid > div:nth-child(2),
          .footer-grid > div:nth-child(3) {
            padding-left: 2rem !important;
          }
          
          footer { padding: 40px 1.5rem 30px !important; }
        }

        @media (min-width: 768px) {
          .footer-grid { grid-template-columns: 1.5fr 1fr 1fr 1.5fr !important; }
          .footer-bottom { flex-direction: row !important; }
        }
      `}</style>

      <div ref={containerRef} className="footer-wrapper">
        <div className="footer-bg-animation">
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="blob blob-3" />
        </div>
        <motion.footer
          style={{
            position: 'relative',
            zIndex: 10,
            background: 'transparent',
            padding: '40px 2rem 36px',
            y: contentY,
            opacity: opacity,
            scale: scale
          }}
        >
          <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 10 }}>

            {/* Main grid */}
            <div className="footer-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '2.5rem',
              marginBottom: '3rem',
              alignItems: 'start',
            }}>

              {/* Brand column */}
              <div className="footer-brand-col" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ textDecoration: 'none' }}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src={logoImg} alt="FlyingSlap Logo" loading="eager" fetchPriority="high" decoding="sync" style={{ width: 52, height: 52, objectFit: 'contain' }} />
                    <span style={{
                      fontSize: 20, fontWeight: 900, letterSpacing: '-0.04em',
                      textTransform: 'uppercase', fontFamily: "var(--font-headline)", lineHeight: 1
                    }}>
                      <span style={{ color: '#0f2d5e' }}>FLYING</span><span style={{ color: '#e65100' }}>SLAP</span>
                    </span>
                  </motion.div>
                </Link>
                <p style={{ color: '#0f2d5e', fontSize: '0.875rem', lineHeight: 1.6, maxWidth: 300, fontWeight: 600 }}>
                  Drone futbolunun geleceği burada. Teknoloji, strateji ve heyecanın buluşma noktası FlyingSlap ile gökyüzüne hakim olun.
                </p>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <a href="https://instagram.com/flyingslap" target="_blank" rel="noopener noreferrer" className="footer-social-btn">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Sayfalar links */}
              <div>
                <h4 style={{
                  color: '#e65100', fontWeight: 800,
                  letterSpacing: '0.15em', fontSize: 11, marginBottom: '1rem',
                  fontFamily: "var(--font-headline)",
                  paddingTop: '0.75rem'
                }}>
                  SAYFALAR
                </h4>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/" className="footer-link">Ana Sayfa</Link></motion.div></li>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/hakkimizda" className="footer-link">Hakkımızda</Link></motion.div></li>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/nasil-katilabilirsiniz" className="footer-link">Nasıl Katılabilirsiniz?</Link></motion.div></li>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/iletisim" className="footer-link">İletişim</Link></motion.div></li>
                </ul>
              </div>

              {/* Hızlı Erişim links */}
              <div>
                <h4 style={{
                  color: '#e65100', fontWeight: 800,
                  letterSpacing: '0.15em', fontSize: 11, marginBottom: '1rem',
                  fontFamily: "var(--font-headline)",
                  paddingTop: '0.75rem'
                }}>
                  HIZLI ERİŞİM
                </h4>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.75rem', color: '#0f2d5e' }}>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/yonetim-kurulumuz" className="footer-link">Yönetim Kurulumuz</Link></motion.div></li>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/tuzugumuz" className="footer-link">Tüzüğümüz</Link></motion.div></li>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/yarisma-takvimi" className="footer-link">Yarışma Takvimi</Link></motion.div></li>
                  <li><motion.div whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}><Link to="/nasil-katilabilirsiniz#kademeli-dronelar" className="footer-link">Drone Eğitimlerimiz</Link></motion.div></li>
                </ul>
              </div>

              {/* Course Enrollment */}
              <div className="footer-course-col">
                <h4 style={{
                  color: '#e65100', fontWeight: 800,
                  letterSpacing: '0.15em', fontSize: 11, marginBottom: '1rem',
                  fontFamily: "var(--font-headline)",
                  paddingTop: '0.75rem'
                }}>
                  EĞİTİM PROGRAMI
                </h4>
                <p style={{ color: '#0f2d5e', fontSize: '0.875rem', marginBottom: '1.25rem', lineHeight: 1.5, fontWeight: 600 }}>
                  Drone kurslarımıza başvuru yapmak için aşağıdaki butona tıklayın.
                </p>
                <button
                  className="footer-submit"
                  onClick={() => setIsAppModalOpen(true)}
                  style={{
                    borderRadius: '10px', width: 'auto', padding: '0 24px', height: '38px',
                    fontSize: '11px', fontWeight: 800, letterSpacing: '0.15em',
                    boxShadow: '0 10px 20px rgba(230,81,0,0.15)',
                  }}
                >
                  BAŞVUR
                </button>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="footer-bottom" style={{
              borderTop: '1px solid #f1f5f9',
              paddingTop: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5rem',
            }}>
              <p className="footer-bottom-text" style={{
                color: '#0f2d5e', fontSize: 10,
                fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.1em', fontFamily: "var(--font-body)",
                textAlign: 'center'
              }}>
                <span style={{ color: '#e65100' }}>Copyright © 2026</span> Tüm Haklarımız Saklıdır.
              </p>
            </div>

          </div>
        </motion.footer>
      </div>

      {/* Application Modal */}
      <ApplicationModal isOpen={isAppModalOpen} onClose={() => setIsAppModalOpen(false)} />
    </>
  );
};

export default Footer;
